import { z } from "zod";

/** Área de produto — metadado só de código (nunca vira coluna no banco), usado pra agrupar a tela
 *  de admin. Plugar um módulo novo (ex: Radiologia) é só declarar a área aqui + as features com
 *  `area` apontando pra ela — a tela de admin agrupa sozinha. */
export const FEATURE_AREAS = ["clinica", "laboratorio", "radiologia"] as const;
export type FeatureArea = (typeof FEATURE_AREAS)[number];

export const FEATURE_AREA_LABELS: Record<FeatureArea, string> = {
  clinica: "Clínica",
  laboratorio: "Laboratório",
  radiologia: "Radiologia",
};

/** Disponibilidade de uma feature — um valor só por feature (não um por plano), com ordem
 *  NONE < PAID_ONLY < ALL_PLANS. Isso torna "FREE tem, PAID não tem" irrepresentável por
 *  construção, em vez de precisar validar contra esse estado incoerente. */
export const FEATURE_AVAILABILITY = ["NONE", "PAID_ONLY", "ALL_PLANS"] as const;
export type FeatureAvailability = (typeof FEATURE_AVAILABILITY)[number];

export function isEnabledForPlan(availability: FeatureAvailability, plan: "FREE" | "PAID"): boolean {
  if (availability === "NONE") return false;
  if (availability === "ALL_PLANS") return true;
  return plan === "PAID"; // PAID_ONLY
}

type FeatureDefinition = {
  key: string;
  area: FeatureArea;
  label: string;
  /** Itens que essa feature realmente cobre — a tela de admin renderiza como chips, não como
   *  frase corrida. Um item só (ex: `["Lembrete automático de consulta"]`) é perfeitamente válido
   *  pra feature de escopo único. */
  topics: readonly string[];
  /** false = não existe implementação real atrás da feature ainda (placeholder). Uma feature
   *  "planned" retorna sempre false, pra qualquer plano — o resolver nem olha o banco pra essas,
   *  e o admin não consegue mudar isso mesmo chamando a mutation direto. */
  status: "active" | "planned";
};

export const FEATURE_DEFINITIONS = [
  {
    key: "customization",
    area: "laboratorio",
    label: "Personalização de produção",
    topics: [
      "Estágios de produção",
      "Modelo",
      "Catálogo customizado",
      "Tabelas de preço",
      "Papéis customizados",
    ],
    status: "active",
  },
  {
    key: "clinicServices",
    area: "clinica",
    label: "Catálogo de serviços e procedimentos",
    topics: [
      "Lista de preços por procedimento",
      "Vínculo de serviço na consulta",
      "Tipos de procedimento personalizados na agenda",
    ],
    status: "active",
  },
  {
    key: "lab.publicSearch",
    area: "laboratorio",
    label: "Busca pública do laboratório",
    topics: ["Aparece na busca de parceiros de clínicas"],
    status: "active",
  },
  {
    key: "agenda.whatsappReminder",
    area: "clinica",
    label: "Lembrete por WhatsApp",
    topics: ["Lembrete automático de consulta"],
    status: "active",
  },
  {
    key: "receituario",
    area: "clinica",
    label: "Receituário",
    topics: ["Página de receituário no menu"],
    status: "planned",
  },
  {
    key: "patient.advancedTabs",
    area: "clinica",
    label: "Abas avançadas da ficha do paciente",
    topics: [
      "Anamnese",
      "Odontograma",
      "Financeiro",
      "Solicitar exames",
      "Novo tratamento",
      "Receita médica",
    ],
    status: "planned",
  },
  {
    // Começa desligada (NONE) em produção: liga no admin depois que um cirurgião-dentista validar
    // as perguntas do questionário padrão.
    key: "patient.anamnese",
    area: "clinica",
    label: "Anamnese do paciente",
    topics: ["Aba Anamnese na ficha", "Link para o paciente preencher", "Alertas clínicos na ficha e na agenda"],
    status: "active",
  },
  {
    key: "patient.treatmentPlans",
    area: "clinica",
    label: "Plano de tratamento e orçamento",
    topics: ["Aba Tratamentos na ficha", "Orçamento impresso", "Pedido de prótese a partir do plano"],
    status: "active",
  },
  {
    // Separada de `patient.advancedTabs` em 2026-08: Arquivos estava empacotada com abas que são
    // placeholder, e a feature inteira é `planned` (sempre desligada) — então uma aba pronta de
    // ponta a ponta (upload assinado, versão, tags, compartilhamento) ficava invisível por
    // carona. Sozinha ela é `active` de verdade.
    key: "patient.files",
    area: "clinica",
    label: "Arquivos do paciente",
    topics: ["Aba Arquivos na ficha", "Exames e documentos anexados ao paciente"],
    status: "active",
  },
  {
    // Estrutura reservada em 2026-08 pro assistente de ditado, com a implementação adiada até a
    // LGPD estar resolvida (dado clínico indo pra transcritor terceiro). Como `planned`, o
    // resolver devolve false pra qualquer plano — nem o painel admin liga por engano — e o botão
    // aparece anunciando "em breve". Quando destravar, vira `active` e o motor pluga no botão.
    key: "atendimento.ditado",
    area: "clinica",
    label: "Observações ditadas no atendimento",
    topics: ["Ditar a observação da consulta em vez de digitar"],
    status: "planned",
  },
  {
    key: "salas",
    area: "clinica",
    label: "Salas e planta da clínica",
    topics: ["Salas na consulta, com conflito de cadeira", "Planta da clínica ao vivo", "Agenda por sala", "Modo recepção"],
    status: "active",
  },
  {
    key: "financeiro",
    area: "laboratorio",
    label: "Financeiro",
    topics: ["Visão do mês com margem e resultado", "A receber e a pagar", "Custos fixos", "Notas fiscais"],
    status: "active",
  },
] as const satisfies readonly FeatureDefinition[];

export type FeatureKey = (typeof FEATURE_DEFINITIONS)[number]["key"];

// Tupla não-vazia (não só `FeatureKey[]`) de propósito — z.enum() exige `[string, ...string[]]`,
// e um array plano vindo de .map() não satisfaz esse tipo sozinho.
export const FEATURE_KEYS = FEATURE_DEFINITIONS.map((d) => d.key) as [FeatureKey, ...FeatureKey[]];

export const featureKeySchema = z.enum(FEATURE_KEYS);
export const featureAvailabilitySchema = z.enum(FEATURE_AVAILABILITY);

export function getFeatureDefinition(key: FeatureKey): FeatureDefinition {
  const def = FEATURE_DEFINITIONS.find((d) => d.key === key);
  if (!def) throw new Error(`Feature desconhecida: ${key}`);
  return def;
}
